import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsive } from '../../hooks/useResponsive';
import api from '../../services/api';
import { describeApiError } from '../../utils/apiErrors';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const { theme } = useTheme();
  // Responsive breakpoint — collapses the marketing column and stacks
  // the form full-width on phones / narrow tablets. Without this the
  // 520px-fixed right panel was crashing into the flex:1 left panel
  // on anything < ~1024px wide.
  const { isMobile, isTablet } = useResponsive();
  const narrow = isMobile || isTablet;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Forgot password states
  const [forgotMode, setForgotMode] = useState<'off' | 'email' | 'otp' | 'newpass' | 'done'>('off');
  const [resetEmail, setResetEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleForgotSubmitEmail = async () => {
    if (!resetEmail.trim()) { setForgotError('Please enter your email'); return; }
    setForgotLoading(true); setForgotError('');
    try {
      await api.forgotPassword(resetEmail.trim());
      setForgotSuccess('OTP sent to your email');
      setForgotMode('otp');
    } catch (err: any) {
      // describeApiError surfaces the 502 ("email service issue") path
      // distinctly from 4xx so the user knows whether to retry or call IT.
      setForgotError(describeApiError(err, 'Failed to send OTP'));
    } finally { setForgotLoading(false); }
  };

  const handleForgotSubmitOTP = async () => {
    if (!otpCode.trim()) { setForgotError('Please enter the OTP code'); return; }
    setForgotError('');
    setForgotSuccess('');
    setForgotMode('newpass');
  };

  const handleResetPassword = async () => {
    if (!newPassword) { setForgotError('Please enter a new password'); return; }
    if (newPassword.length < 6) { setForgotError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setForgotError('Passwords do not match'); return; }
    setForgotLoading(true); setForgotError('');
    try {
      await api.resetPassword(resetEmail.trim(), otpCode.trim(), newPassword);
      setForgotMode('done');
      setForgotSuccess('Password reset successful! You can now sign in.');
    } catch (err: any) {
      setForgotError(describeApiError(err, 'Failed to reset password'));
    } finally { setForgotLoading(false); }
  };

  const resetForgotState = () => {
    setForgotMode('off');
    setResetEmail(''); setOtpCode(''); setNewPassword(''); setConfirmPassword('');
    setForgotError(''); setForgotSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // SVG Icons as components
  const InventoryIcon = () => (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
      <rect x="5" y="5" width="4" height="4"/>
      <rect x="15" y="5" width="4" height="4"/>
    </svg>
  );

  const TruckIcon = () => (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <rect x="1" y="3" width="15" height="13"/>
      <polygon points="16 8 20 8 23 11 23 16"/>
      <circle cx="5.5" cy="18.5" r="2.5"/>
      <circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  );

  const BrainIcon = () => (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M12 5a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3 1 1 0 0 0 1-1v-6a1 1 0 0 0-1-1"/>
      <path d="M9 5a2 2 0 0 1 2 2"/>
      <path d="M15 5a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3 1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1"/>
      <path d="M15 5a2 2 0 0 0-2 2"/>
      <path d="M12 9v6m4-6v6m-8 0a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1"/>
      <path d="M6 8a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1z"/>
    </svg>
  );

  const features = [
    {
      icon: InventoryIcon,
      label: 'Real-time Inventory',
      desc: 'Track stock levels across all warehouses',
      color: '#2196f3',
    },
    {
      icon: TruckIcon,
      label: 'Booking & Gate Management',
      desc: 'QR-based gate passes with instant approvals',
      color: '#42a5f5',
    },
    {
      icon: BrainIcon,
      label: 'AI-Powered Analytics',
      desc: 'Natural language queries powered by Halley AI',
      color: '#1e88e5',
    },
  ];

  const particleCount = 8;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        // Stack vertically on phones/tablets; side-by-side on desktop.
        flexDirection: narrow ? 'column' : 'row',
        background: 'linear-gradient(135deg, #0a1628 0%, #0f2240 30%, #132d52 60%, #0a1628 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background particles */}
      {Array.from({ length: particleCount }).map((_, i) => (
        <div
          key={`particle-${i}`}
          style={{
            position: 'absolute',
            width: Math.random() * 4 + 2,
            height: Math.random() * 4 + 2,
            borderRadius: '50%',
            background: `rgba(33, 150, 243, ${Math.random() * 0.5 + 0.2})`,
            left: `${Math.random() * 100}%`,
            bottom: '-10px',
            animation: `float-particle ${Math.random() * 8 + 10}s infinite ease-in`,
            animationDelay: `${i * 0.2}s`,
            filter: 'blur(0.5px)',
          }}
        />
      ))}

      {/* Glowing background orbs */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33,150,243,0.12) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33,150,243,0.08) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(21,101,192,0.06) 0%, transparent 60%)',
          transform: 'translate(-50%, -50%)',
          filter: 'blur(40px)',
        }}
      />

      {/* Left branding panel — hidden on narrow viewports so the
          login form gets the full screen. Marketing copy is nice-to-
          have on desktop, not essential. */}
      <div
        style={{
          flex: 1,
          display: narrow ? 'none' : 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '60px 80px',
          position: 'relative',
          zIndex: 1,
          minWidth: 0,
        }}
      >
        {/* HALA Logo with glow animation */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginBottom: 48,
            animation: mounted ? 'fadeInStagger 0.8s ease-out' : 'none',
          }}
        >
          <div style={{ position: 'relative' }}>
            <img
              src="/hala-logo.png"
              alt="HALA Logo"
              width={72}
              height={72}
              style={{
                filter: 'drop-shadow(0 0 20px rgba(33, 150, 243, 0.6))',
                animation: 'logoGlow 3s ease-in-out infinite',
                objectFit: 'contain',
              }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 900,
                color: '#fff',
                letterSpacing: 4,
                background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              HALA
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.4)',
                letterSpacing: 2.5,
                textTransform: 'uppercase',
                marginTop: 2,
                fontWeight: 600,
              }}
            >
              Supply Chain Services
            </div>
          </div>
        </div>

        {/* Main heading */}
        <h1
          style={{
            fontSize: 48,
            fontWeight: 900,
            color: '#fff',
            lineHeight: 1.15,
            marginBottom: 20,
            maxWidth: 520,
            animation: mounted ? 'fadeInStagger 0.8s ease-out 0.1s both' : 'none',
            letterSpacing: -0.5,
          }}
        >
          Empowering Your Logistics Journey
        </h1>

        {/* Subtitle */}
        <p
          style={{
            fontSize: 16,
            color: 'rgba(255,255,255,0.5)',
            lineHeight: 1.6,
            maxWidth: 480,
            marginBottom: 48,
            animation: mounted ? 'fadeInStagger 0.8s ease-out 0.2s both' : 'none',
          }}
        >
          Manage inventory, track shipments, generate gate passes, and monitor warehouse operations — all powered by Halley AI.
        </p>

        {/* Feature cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {features.map((feature, i) => {
            const IconComponent = feature.icon;
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 18,
                  padding: 20,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 16,
                  border: '1px solid rgba(33, 150, 243, 0.2)',
                  borderLeft: `4px solid ${feature.color}`,
                  animation: mounted ? `fadeInStagger 0.8s ease-out ${0.3 + i * 0.1}s both` : 'none',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  transform: 'translateX(0)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(33, 150, 243, 0.4)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(33, 150, 243, 0.2)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: `rgba(33, 150, 243, 0.15)`,
                    border: `1px solid rgba(33, 150, 243, 0.25)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: feature.color,
                  }}
                >
                  <IconComponent />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: 'rgba(255,255,255,0.95)',
                      marginBottom: 4,
                    }}
                  >
                    {feature.label}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.4)',
                      lineHeight: 1.5,
                    }}
                  >
                    {feature.desc}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right login panel — full width on narrow viewports, fixed
          520px column on desktop. */}
      <div
        style={{
          width: narrow ? '100%' : 520,
          minHeight: narrow ? '100vh' : 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: narrow ? '24px 16px' : '40px 48px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 420,
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: 24,
            padding: '48px 40px',
            boxShadow:
              '0 20px 60px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
            animation: mounted ? 'slideUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
          }}
        >
          {/* Card header with logo */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 32,
            }}
          >
            <img
              src="/hala-logo.png"
              alt="HALA"
              width={48}
              height={48}
              style={{
                filter: 'drop-shadow(0 0 12px rgba(33, 150, 243, 0.4))',
                objectFit: 'contain',
              }}
            />
          </div>

          {/* Welcome text */}
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h2
              style={{
                fontSize: 24,
                fontWeight: 800,
                color: '#fff',
                marginBottom: 8,
                letterSpacing: -0.5,
              }}
            >
              Welcome Back
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, lineHeight: 1.5 }}>
              Sign in to continue to your workspace
            </p>
          </div>

          {/* Error banner */}
          {error && (
            <div
              style={{
                background: 'rgba(220, 38, 38, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                borderRadius: 12,
                padding: '12px 16px',
                marginBottom: 24,
                color: '#ff6b6b',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                animation: 'slideUp 0.4s ease-out',
              }}
            >
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Username field */}
            <div style={{ marginBottom: 22 }}>
              <label
                style={{
                  display: 'block',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 12,
                  fontWeight: 700,
                  marginBottom: 10,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Username
              </label>
              <div style={{ position: 'relative' }}>
                <svg
                  width={18}
                  height={18}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  style={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '14px 16px 14px 48px',
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)',
                  }}
                  onFocus={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.borderColor = 'rgba(33, 150, 243, 0.6)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(33, 150, 243, 0.2), inset 0 1px 2px rgba(0, 0, 0, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.06)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.12)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Password field */}
            <div style={{ marginBottom: 28 }}>
              <label
                style={{
                  display: 'block',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 12,
                  fontWeight: 700,
                  marginBottom: 10,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <svg
                  width={18}
                  height={18}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  style={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 48px 14px 48px',
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)',
                  }}
                  onFocus={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.borderColor = 'rgba(33, 150, 243, 0.6)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(33, 150, 243, 0.2), inset 0 1px 2px rgba(0, 0, 0, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.06)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.12)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 14,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 6,
                    opacity: 0.5,
                    transition: 'opacity 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.opacity = '0.8';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.opacity = '0.5';
                  }}
                >
                  <svg
                    width={18}
                    height={18}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="rgba(255,255,255,0.5)"
                    strokeWidth={2}
                    strokeLinecap="round"
                  >
                    {showPassword ? (
                      <>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </>
                    ) : (
                      <>
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Forgot password link */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, marginTop: -16 }}>
              <button
                type="button"
                onClick={() => { setForgotMode('email'); setError(''); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'rgba(33, 150, 243, 0.8)', fontSize: 12, fontWeight: 600,
                  padding: '4px 0', transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#2196f3'; }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'rgba(33, 150, 243, 0.8)'; }}
              >
                Forgot Password?
              </button>
            </div>

            {/* Sign In button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: loading
                  ? 'linear-gradient(135deg, #1565c0 0%, #2196f3 100%)'
                  : 'linear-gradient(135deg, #1565c0 0%, #2196f3 100%)',
                borderRadius: 12,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: loading
                  ? '0 4px 20px rgba(33, 150, 243, 0.4)'
                  : '0 8px 28px rgba(33, 150, 243, 0.35)',
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                position: 'relative',
                overflow: 'hidden',
                opacity: loading ? 0.9 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(33, 150, 243, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '0 8px 28px rgba(33, 150, 243, 0.35)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {loading ? (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                  }}
                >
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    style={{ animation: 'spin 1s linear infinite' }}
                  >
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                <span style={{ position: 'relative', zIndex: 1 }}>Sign In</span>
              )}
            </button>
          </form>

          {/* Footer */}
          <div style={{ marginTop: 36, textAlign: 'center' }}>
            <p
              style={{
                fontSize: 12,
                color: 'rgba(255,255,255,0.4)',
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              Powered by{' '}
              <span
                style={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                HALA
              </span>{' '}
              Supply Chain Services
            </p>
            <p
              style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.3)',
                marginTop: 6,
                margin: 0,
              }}
            >
              v2.0
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal Overlay */}
      {forgotMode !== 'off' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'fadeInOverlay 0.3s ease',
        }}>
          <div style={{
            width: 420, maxWidth: '90vw',
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 24, padding: '40px 36px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
            animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12,
                  background: 'rgba(33,150,243,0.15)', border: '1px solid rgba(33,150,243,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#2196f3" strokeWidth={2} strokeLinecap="round">
                    {forgotMode === 'done' ? (
                      <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>
                    ) : (
                      <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></>
                    )}
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>
                    {forgotMode === 'email' && 'Forgot Password'}
                    {forgotMode === 'otp' && 'Enter OTP'}
                    {forgotMode === 'newpass' && 'New Password'}
                    {forgotMode === 'done' && 'All Done!'}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                    {forgotMode === 'email' && 'Enter your email to receive a reset code'}
                    {forgotMode === 'otp' && 'Check your email for the verification code'}
                    {forgotMode === 'newpass' && 'Choose a new secure password'}
                    {forgotMode === 'done' && 'Your password has been updated'}
                  </div>
                </div>
              </div>
              <button onClick={resetForgotState} style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'rgba(255,255,255,0.5)', transition: 'all 0.2s',
              }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>

            {/* Progress steps */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 28 }}>
              {['email', 'otp', 'newpass', 'done'].map((step, i) => {
                const steps = ['email', 'otp', 'newpass', 'done'];
                const currentIdx = steps.indexOf(forgotMode);
                const isDone = i < currentIdx;
                const isActive = i === currentIdx;
                return (
                  <React.Fragment key={step}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: isDone ? '#2196f3' : isActive ? 'rgba(33,150,243,0.2)' : 'rgba(255,255,255,0.06)',
                      border: isActive ? '2px solid #2196f3' : isDone ? '2px solid #2196f3' : '2px solid rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                      color: isDone ? '#fff' : isActive ? '#2196f3' : 'rgba(255,255,255,0.3)',
                      transition: 'all 0.3s',
                    }}>
                      {isDone ? (
                        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><polyline points="20 6 9 17 4 12"/></svg>
                      ) : i + 1}
                    </div>
                    {i < 3 && (
                      <div style={{ flex: 1, height: 2, borderRadius: 1, background: isDone ? '#2196f3' : 'rgba(255,255,255,0.08)', transition: 'all 0.3s' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Error / Success messages */}
            {forgotError && (
              <div style={{
                background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)',
                borderRadius: 12, padding: '10px 14px', marginBottom: 20,
                color: '#ff6b6b', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                {forgotError}
              </div>
            )}
            {forgotSuccess && forgotMode !== 'done' && (
              <div style={{
                background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                borderRadius: 12, padding: '10px 14px', marginBottom: 20,
                color: '#4ade80', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                {forgotSuccess}
              </div>
            )}

            {/* Step 1: Email */}
            {forgotMode === 'email' && (
              <div>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Email Address</label>
                <div style={{ position: 'relative', marginBottom: 24 }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={2} strokeLinecap="round" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}>
                    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/>
                  </svg>
                  <input
                    type="email" value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleForgotSubmitEmail(); }}
                    placeholder="Enter your registered email"
                    autoFocus
                    style={{
                      width: '100%', padding: '14px 16px 14px 48px',
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 12, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                      transition: 'all 0.3s', backdropFilter: 'blur(10px)',
                    }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(33,150,243,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(33,150,243,0.2)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <button onClick={handleForgotSubmitEmail} disabled={forgotLoading} style={{
                  width: '100%', padding: '14px', borderRadius: 12,
                  background: 'linear-gradient(135deg, #1565c0, #2196f3)', color: '#fff',
                  fontSize: 14, fontWeight: 700, border: 'none', cursor: forgotLoading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 20px rgba(33,150,243,0.35)', transition: 'all 0.3s',
                  opacity: forgotLoading ? 0.8 : 1, letterSpacing: 0.5,
                }}>
                  {forgotLoading ? 'Sending OTP...' : 'Send Reset Code'}
                </button>
              </div>
            )}

            {/* Step 2: OTP */}
            {forgotMode === 'otp' && (
              <div>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Verification Code (OTP)</label>
                <div style={{ position: 'relative', marginBottom: 24 }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={2} strokeLinecap="round" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}>
                    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M12 12h.01M8 12h.01M16 12h.01"/>
                  </svg>
                  <input
                    type="text" value={otpCode}
                    onChange={e => setOtpCode(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleForgotSubmitOTP(); }}
                    placeholder="Enter 6-digit code"
                    autoFocus maxLength={6}
                    style={{
                      width: '100%', padding: '14px 16px 14px 48px',
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 12, color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: 8,
                      outline: 'none', boxSizing: 'border-box', transition: 'all 0.3s',
                      textAlign: 'center', fontFamily: 'var(--mono)',
                    }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(33,150,243,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(33,150,243,0.2)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => { setForgotMode('email'); setForgotError(''); setForgotSuccess(''); }} style={{
                    flex: 1, padding: '14px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s',
                  }}>Back</button>
                  <button onClick={handleForgotSubmitOTP} style={{
                    flex: 2, padding: '14px', borderRadius: 12,
                    background: 'linear-gradient(135deg, #1565c0, #2196f3)', color: '#fff',
                    fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
                    boxShadow: '0 4px 20px rgba(33,150,243,0.35)', transition: 'all 0.3s', letterSpacing: 0.5,
                  }}>Verify Code</button>
                </div>
                <button onClick={handleForgotSubmitEmail} disabled={forgotLoading} style={{
                  width: '100%', marginTop: 12, padding: '10px', borderRadius: 8,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'rgba(33,150,243,0.7)', fontSize: 12, fontWeight: 600, transition: 'color 0.2s',
                }}>{forgotLoading ? 'Sending...' : 'Resend Code'}</button>
              </div>
            )}

            {/* Step 3: New Password */}
            {forgotMode === 'newpass' && (
              <div>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>New Password</label>
                <div style={{ position: 'relative', marginBottom: 16 }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={2} strokeLinecap="round" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}>
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                  <input
                    type="password" value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    autoFocus
                    style={{
                      width: '100%', padding: '14px 16px 14px 48px',
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 12, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                      transition: 'all 0.3s',
                    }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(33,150,243,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(33,150,243,0.2)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Confirm Password</label>
                <div style={{ position: 'relative', marginBottom: 24 }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={2} strokeLinecap="round" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <input
                    type="password" value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleResetPassword(); }}
                    placeholder="Confirm new password"
                    style={{
                      width: '100%', padding: '14px 16px 14px 48px',
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 12, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                      transition: 'all 0.3s',
                    }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(33,150,243,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(33,150,243,0.2)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                {/* Password strength hint */}
                {newPassword && (
                  <div style={{ marginBottom: 16, display: 'flex', gap: 4 }}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{
                        flex: 1, height: 3, borderRadius: 2,
                        background: newPassword.length >= i * 3
                          ? (newPassword.length >= 12 ? '#22c55e' : newPassword.length >= 8 ? '#f59e0b' : '#ef4444')
                          : 'rgba(255,255,255,0.08)',
                        transition: 'all 0.3s',
                      }} />
                    ))}
                  </div>
                )}
                <button onClick={handleResetPassword} disabled={forgotLoading} style={{
                  width: '100%', padding: '14px', borderRadius: 12,
                  background: 'linear-gradient(135deg, #1565c0, #2196f3)', color: '#fff',
                  fontSize: 14, fontWeight: 700, border: 'none', cursor: forgotLoading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 20px rgba(33,150,243,0.35)', transition: 'all 0.3s',
                  opacity: forgotLoading ? 0.8 : 1, letterSpacing: 0.5,
                }}>
                  {forgotLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            )}

            {/* Step 4: Done */}
            {forgotMode === 'done' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', margin: '0 auto 20px',
                  background: 'rgba(34,197,94,0.15)', border: '2px solid rgba(34,197,94,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth={2.5} strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Password Updated!</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 28, lineHeight: 1.5 }}>
                  Your password has been successfully reset. You can now sign in with your new password.
                </div>
                <button onClick={resetForgotState} style={{
                  width: '100%', padding: '14px', borderRadius: 12,
                  background: 'linear-gradient(135deg, #1565c0, #2196f3)', color: '#fff',
                  fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
                  boxShadow: '0 4px 20px rgba(33,150,243,0.35)', letterSpacing: 0.5,
                }}>Back to Sign In</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSS animations and keyframes */}
      <style>{`
        @keyframes float-particle {
          0% {
            bottom: -10px;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            bottom: 110vh;
            opacity: 0;
            transform: translateX(100px);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInStagger {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes logoGlow {
          0%, 100% {
            filter: drop-shadow(0 0 20px rgba(33, 150, 243, 0.6));
          }
          50% {
            filter: drop-shadow(0 0 30px rgba(33, 150, 243, 0.8));
          }
        }

        @keyframes buttonShine {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        @keyframes fadeInOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
